'use client';
import { motion } from 'motion/react';
import { CheckCircle2 } from 'lucide-react';

const steps = [
  {
    num: '01',
    label: 'Briefing',
    question: 'Am I on track? What needs attention?',
    time: '1–2 min',
  },
  {
    num: '02',
    label: 'Explore',
    question: 'Show me the data. Who is failing?',
    time: '2–3 min',
  },
  {
    num: '03',
    label: 'Ask',
    question: 'Why is this happening? What if I try X?',
    time: '3–5 min',
  },
  {
    num: '04',
    label: 'Act',
    question: 'What action should I take? Send it.',
    time: '2–3 min',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

export default function CognitiveLoop() {
  return (
    <section
      className="relative overflow-hidden border-y border-border bg-white py-24 sm:py-32"
      aria-labelledby="solution-heading"
    >
      {/* Subtle amber glow top-right */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse at 85% 10%, hsl(30 95% 52% / 0.04), transparent 50%)',
        }}
      />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        {/* Header */}
        <motion.div
          className="max-w-2xl"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          variants={fadeUp}
        >
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
            The Intelligence Loop
          </p>
          <h2
            id="solution-heading"
            className="mt-4 font-serif text-3xl font-700 leading-tight text-foreground sm:text-4xl md:text-5xl"
          >
            Four questions.
            <br />
            Ten minutes. Done.
          </h2>
        </motion.div>

        {/* The list */}
        <div className="mt-14 sm:mt-20">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              className="group border-t border-border transition-colors hover:bg-accent/[0.03]"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              variants={fadeUp}
            >
              <div className="flex items-baseline gap-6 py-7 sm:gap-10 sm:py-9 md:py-10">
                {/* Number — amber accent */}
                <span className="shrink-0 font-serif text-2xl font-700 text-accent/30 transition-colors group-hover:text-accent/60 sm:text-3xl">
                  {step.num}
                </span>

                {/* Label */}
                <span className="w-24 shrink-0 text-base font-semibold text-foreground sm:w-36 sm:text-lg md:text-xl">
                  {step.label}
                </span>

                {/* Question */}
                <span className="hidden flex-1 text-base text-muted-foreground sm:block md:text-lg">
                  {step.question}
                </span>

                {/* Time */}
                <span className="ml-auto shrink-0 font-mono text-sm text-muted-foreground/50">
                  {step.time}
                </span>
              </div>

              {/* Mobile question */}
              <p className="-mt-2 pb-6 pl-[calc(theme(fontSize.2xl)+theme(spacing.6)+theme(spacing.6))] text-sm text-muted-foreground sm:hidden">
                {step.question}
              </p>
            </motion.div>
          ))}

          <div className="border-t border-border" />

          {/* Punchline */}
          <motion.div
            className="flex items-center gap-3 pt-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.3 }}
            variants={fadeUp}
          >
            <CheckCircle2
              className="h-5 w-5 text-emerald-600"
              aria-hidden="true"
            />
            <p className="text-base font-semibold text-foreground sm:text-lg">
              Total: 10 minutes. Every crore accounted for.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
